import { useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Card } from '@/shared/ui/Card';
import { DataTable } from '@/shared/ui/DataTable';
import { EmptyState } from '@/shared/ui/EmptyState';
import { SkeletonRows } from '@/shared/ui/Skeleton';
import { StatusBadge } from '@/shared/ui/StatusBadge';

import type { Execution } from '@/shared/api/generated/rules';
import {
  useExecutionTasks,
  useExecutionViolations,
  useSamplePolicy,
  useViolationSample,
} from '../api/rules';
import {
  sampleAbsenceKey,
  sampleColumns,
  samplePolicyKey,
  severityTone,
} from '../model/presentation';
import { ResolutionSqlPanel } from './ResolutionSqlPanel';

/* One execution's violations, and the masked sample behind one of them.
 *
 * This is the last step of rung 4's flow — source data → masking at the source → sealed artifact →
 * reference → **a person** — and three things about it are deliberate.
 *
 * **A sample is fetched only when somebody asks.** The read is authorized under its own permission and
 * written to the audit chain, so fetching one because a row rendered would put a name in a seven-year
 * record for a screen nobody read. One row's sample is open at a time.
 *
 * **A digest and a preview, never a value.** The artifact holds only `<col>_digest` and
 * `<col>_preview`; no code path in this component could render anything else, because there is nothing
 * else in the data. The header is derived from the rows for the same reason — one built from a separate
 * source could name a column no cell holds.
 *
 * **An absence always says which absence.** `withheld`, `expired` and `none_captured` are three facts
 * about a customer, and a shared "no samples" would collapse the tenant's own policy decision into our
 * missing configuration. The tenant's policy is shown above them, because "why does this violation have
 * no example rows" is asked here and has to be answerable here.
 */

function SampleRows({ violationId }: { readonly violationId: string }): ReactElement {
  const { t } = useTranslation();
  const sample = useViolationSample(violationId);

  if (sample.isPending) {
    return (
      <p role="status" className="lq-muted">
        {t('violations.sample.loading')}
      </p>
    );
  }

  if (sample.isError) {
    /* The server's own sentence, repeated rather than summarised. A 409 here is an answer — withheld,
       expired, or never captured — and each names what to do about it; a generic "could not load the
       sample" would throw away the only part a person can act on. */
    return <Alert tone="warning">{sample.error.message}</Alert>;
  }

  const columns = sampleColumns(sample.data.rows);

  return (
    <div className="lq-stack" style={{ marginTop: 'var(--space-3)' }}>
      <p className="lq-muted">
        {t('violations.sample.masked')}{' '}
        <span className="lq-dim">
          {t('violations.sample.retention', {
            instant: new Date(sample.data.expires_at),
          })}
        </span>
      </p>
      <DataTable
        caption={t('violations.sample.tableCaption')}
        columns={columns.flatMap((column) => [
          {
            key: column.preview,
            label: t('violations.sample.preview', { column: column.source }),
          },
          {
            key: column.digest,
            label: t('violations.sample.digest', { column: column.source }),
          },
        ])}
      >
        {sample.data.rows.map((row, index) => (
          /* Keyed on the digests, not the array index. A digest is an `md5()` computed at the source,
             so it is stable across refetches and is deliberately *not* a value — which is the only
             reason it is safe to put in the DOM. The index is appended because two rows can share a
             digest when the same bad value appears twice, which is exactly the case the digest exists
             to make visible. */
          <tr
            key={`${columns.map((column) => row[column.digest] ?? '').join('-')}-${String(index)}`}
          >
            {columns.flatMap((column) => [
              <td key={column.preview} className="lq-mono">
                {/* A null stays null rather than becoming stars: the value was absent at the source,
                    which for a `not_null` violation is the finding itself. */}
                {row[column.preview] ?? t('violations.sample.null')}
              </td>,
              <td key={column.digest} className="lq-mono lq-dim">
                {(row[column.digest] ?? '').slice(0, 12)}
              </td>,
            ])}
          </tr>
        ))}
      </DataTable>
      <p className="lq-dim">
        {t('violations.sample.sealed', { kek: sample.data.kek_id })}
      </p>
    </div>
  );
}

/** Zero violations means two different things, and this screen used to say only one of them.
 *
 * "Every rule passed" and "nothing ran" both produce an empty list, and rendering the same
 * congratulatory sentence for both is how a failed execution — a source nobody can reach, a
 * connector this deployment cannot yet execute rules against — reads as a clean bill of health.
 * `may_publish_a_score` is the same signal `scoreBlockedReason` already uses for this exact
 * question, reused here rather than re-derived from task counts a second way.
 */
function wasAnythingMeasured(execution: Execution): boolean {
  return execution.may_publish_a_score;
}

/** The real, per-dataset reason nothing measured — never rendered anywhere until now. `Execution`
 * carries only a summary sentence; the specific cause (a connector with no rule executor, a
 * source refusing the connection) is on the task, in `detail`. */
function TaskFailures({
  executionId,
}: {
  readonly executionId: string;
}): ReactElement | null {
  const { t } = useTranslation();
  const tasks = useExecutionTasks(executionId);

  if (!tasks.isSuccess) return null;
  const failing = tasks.data.items.filter(
    (task) => task.state === 'failed' && task.detail !== null,
  );
  if (failing.length === 0) return null;

  return (
    <div style={{ padding: '0 var(--space-4) var(--space-4)' }}>
      <p className="lq-dim" style={{ fontWeight: 600, margin: '0 0 var(--space-1)' }}>
        {t('violations.taskFailures.title')}
      </p>
      <ul style={{ margin: 0, paddingLeft: 'var(--space-5)' }}>
        {failing.map((task) => (
          <li key={task.id} className="lq-dim">
            {t('violations.taskFailures.entry', {
              dataset: task.qualified_name,
              reason: task.detail,
            })}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function ViolationsPanel({
  executionId,
  execution,
}: {
  readonly executionId: string | undefined;
  /** Optional so a caller that only ever passed an id keeps compiling — but without it this panel
   * cannot tell "measured clean" from "never measured" and falls back to the old, misleading
   * "no violations" reading. Every current caller has the execution in hand already. */
  readonly execution?: Execution | undefined;
}): ReactElement | null {
  const { t } = useTranslation();
  const violations = useExecutionViolations(executionId);
  const policy = useSamplePolicy();
  const [openViolation, setOpenViolation] = useState<string | undefined>(undefined);
  /* Which violation's "Fix SQL" panel is open. Separate from the sample toggle: the sample is an
     audited read of masked rows and the SQL is text about the table, and opening one must not open
     the other. */
  const [fixing, setFixing] = useState<string | undefined>(undefined);

  if (executionId === undefined) return null;
  const measured = execution === undefined ? true : wasAnythingMeasured(execution);

  return (
    <Card
      title={t('violations.title')}
      hint={t('violations.hint')}
      help={t('help.term.violation')}
      flush
    >
      {policy.isSuccess && (
        <div style={{ padding: 'var(--space-4)' }}>
          <p className="lq-muted">
            {t(samplePolicyKey(policy.data.mode, policy.data.declared))}{' '}
            <span className="lq-dim">
              {t('violations.policy.retention', { days: policy.data.retention_days })}
            </span>
          </p>
        </div>
      )}

      {violations.isError && <Alert tone="error">{violations.error.message}</Alert>}

      {violations.isPending && <SkeletonRows rows={3} columns={5} />}

      {violations.isSuccess &&
        (violations.data.items.length === 0 ? (
          measured ? (
            <EmptyState
              title={t('violations.emptyTitle')}
              guidance={t('violations.emptyGuidance')}
            />
          ) : (
            <>
              <EmptyState
                title={t('violations.notMeasured.title')}
                guidance={t('violations.notMeasured.guidance')}
              />
              <TaskFailures executionId={executionId} />
            </>
          )
        ) : (
          <DataTable
            caption={t('violations.tableCaption')}
            columns={[
              { key: 'severity', label: t('violations.column.severity') },
              { key: 'count', label: t('violations.column.count'), numeric: true },
              { key: 'rate', label: t('violations.column.rate'), numeric: true },
              { key: 'columns', label: t('violations.column.columns') },
              { key: 'sample', label: t('violations.column.sample') },
              { key: 'fix', label: t('violations.column.fix'), actions: true },
            ]}
          >
            {violations.data.items.map((violation) => (
              <tr key={violation.id}>
                <td>
                  <StatusBadge
                    tone={severityTone(violation.severity)}
                    label={t(`violations.severity.${violation.severity}`)}
                  />
                </td>
                <td className="lq-table__num lumiq-numeric">
                  {violation.violation_count}
                </td>
                {/* Formatted from the server's number rather than recomputed. Recomputing would let
                    the screen and the stored score disagree about the same run. */}
                <td className="lq-table__num lumiq-numeric">
                  {(violation.failure_rate * 100).toFixed(2)}%
                </td>
                <td className="lq-mono">{violation.columns.join(', ')}</td>
                <td>
                  {violation.sample_state === 'available' ? (
                    <>
                      <Button
                        onClick={() => {
                          setOpenViolation(
                            openViolation === violation.id ? undefined : violation.id,
                          );
                        }}
                      >
                        {openViolation === violation.id
                          ? t('violations.sample.hide')
                          : t('violations.sample.show')}
                      </Button>
                      {openViolation === violation.id && (
                        <SampleRows violationId={violation.id} />
                      )}
                    </>
                  ) : (
                    /* Not a disabled button. A control an operator cannot press teaches nothing; the
                       sentence naming which of the three absences applies is the useful thing. */
                    <span className="lq-muted">
                      {t(sampleAbsenceKey(violation.sample_state))}
                    </span>
                  )}
                </td>
                <td>
                  <Button
                    onClick={() => {
                      setFixing(violation.id);
                    }}
                  >
                    {t('resolution.button.label')}
                  </Button>
                </td>
              </tr>
            ))}
          </DataTable>
        ))}
      <ResolutionSqlPanel
        violationId={fixing}
        onClose={() => {
          setFixing(undefined);
        }}
      />
    </Card>
  );
}
